import rawDialogues from "../data/dialogues.json";
import type { Analect } from "./types";

/** Canonical traditional chapter names — the source titles are inconsistent
 * (mixed simplified/traditional, some missing the name entirely). */
export const CHAPTER_NAMES: Record<number, string> = {
  1: "學而",
  2: "為政",
  3: "八佾",
  4: "里仁",
  5: "公冶長",
  6: "雍也",
  7: "述而",
  8: "泰伯",
  9: "子罕",
  10: "鄉黨",
  11: "先進",
  12: "顏淵",
  13: "子路",
  14: "憲問",
  15: "衛靈公",
  16: "季氏",
  17: "陽貨",
  18: "微子",
  19: "子張",
  20: "堯曰",
};

type RawAnalect = Pick<Analect, "id" | "title" | "text" | "translation" | "annotations">;

function parseNumbers(title: string): { major: number; minor: number } {
  const match = title.match(/(\d+)\.(\d+)/);
  return { major: match ? Number(match[1]) : 0, minor: match ? Number(match[2]) : 0 };
}

const allEntries: Analect[] = (rawDialogues as RawAnalect[])
  .map((item) => {
    const { major, minor } = parseNumbers(item.title);
    return { ...item, major, minor, chapter: CHAPTER_NAMES[major] ?? "全書" };
  })
  .sort((a, b) => a.id - b.id);

/**
 * The source file carries 541 entries but only 512 distinct passages: 鄉黨
 * 10.25–10.27 and the whole of 先進 11.1–11.26 appear twice. 512 is also the
 * canonical chapter count of 楊伯峻《論語譯注》, which is where this text came
 * from — so the duplicates are an import artefact, not a feature of the book.
 *
 * They are collapsed rather than deleted: the later id of each pair stays
 * resolvable as an alias so progress saved against it (and any learning
 * evidence already published to the User Center under `ly:qNNNN:<gate>`)
 * still points at the right passage.
 */
const canonicalByText = new Map<string, number>();
const aliasToCanonical = new Map<number, number>();

for (const entry of allEntries) {
  const key = entry.text.replace(/[⑴-⒇\s]/g, "");
  const first = canonicalByText.get(key);
  if (first === undefined) canonicalByText.set(key, entry.id);
  else aliasToCanonical.set(entry.id, first);
}

export const analects: Analect[] = allEntries.filter((item) => !aliasToCanonical.has(item.id));

/** Duplicate id → the id that survived. Used when migrating saved progress. */
export const ALIAS_IDS: ReadonlyMap<number, number> = aliasToCanonical;

export function canonicalId(id: number): number {
  return aliasToCanonical.get(id) ?? id;
}

export const analectById = new Map<number, Analect>(analects.map((item) => [item.id, item]));
for (const [alias, canonical] of aliasToCanonical) {
  const target = analectById.get(canonical);
  if (target) analectById.set(alias, target);
}

export const chapterNumbers = Array.from(
  new Set(analects.map((item) => item.major).filter((n) => n > 0)),
).sort((a, b) => a - b);

export function chapterName(major: number): string {
  return CHAPTER_NAMES[major] ?? "全書";
}

export function chapterCount(major: number): number {
  return analects.filter((item) => item.major === major).length;
}

export function quoteLabel(item: Analect): string {
  return `${item.chapter} ${item.major}.${item.minor}`;
}
